import React, { useEffect, useState, useRef } from 'react';
import { Plus, Minus, Move } from 'lucide-react';
import { motion } from 'framer-motion';
interface SunRingProps {
  sunPosition: number;
  onSunPositionChange: (degrees: number) => void;
  centerX: number;
  centerY: number;
  radius: number;
  onRadiusChange?: (radius: number) => void;
  onCenterChange?: (center: {
    x: number;
    y: number;
  }) => void;
}
export function SunRing({
  sunPosition,
  onSunPositionChange,
  centerX,
  centerY,
  radius,
  onRadiusChange,
  onCenterChange
}: SunRingProps) {
  const [isDraggingSun, setIsDraggingSun] = useState(false);
  const [isDraggingCenter, setIsDraggingCenter] = useState(false);
  const lastMouseRef = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const screenToSVG = (svg: SVGSVGElement, clientX: number, clientY: number) => {
    const rect = svg.getBoundingClientRect();
    const viewBoxAttr = svg.getAttribute('viewBox');
    if (viewBoxAttr) {
      const [vbX, vbY, vbWidth, vbHeight] = viewBoxAttr.split(' ').map(Number);
      const svgX = vbX + (clientX - rect.left) * (vbWidth / rect.width);
      const svgY = vbY + (clientY - rect.top) * (vbHeight / rect.height);
      return {
        x: svgX,
        y: svgY
      };
    }
    return {
      x: 0,
      y: 0
    };
  };
  const handleCenterMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    const button = e.currentTarget as HTMLButtonElement;
    const foreignObject = button.closest('foreignObject');
    const svg = foreignObject?.ownerSVGElement;
    if (!svg) return;
    svgRef.current = svg;
    const coords = screenToSVG(svg, e.clientX, e.clientY);
    lastMouseRef.current = coords;
    setIsDraggingCenter(true);
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!svgRef.current) return;
      const coords = screenToSVG(svgRef.current, e.clientX, e.clientY);
      if (isDraggingSun) {
        const dx = coords.x - centerX;
        const dy = coords.y - centerY;
        let angle = Math.atan2(dy, dx) * (180 / Math.PI);
        angle = (angle + 360) % 360;
        onSunPositionChange(angle);
      }
      if (isDraggingCenter && lastMouseRef.current && onCenterChange) {
        const deltaX = coords.x - lastMouseRef.current.x;
        const deltaY = coords.y - lastMouseRef.current.y;
        onCenterChange({
          x: centerX + deltaX,
          y: centerY + deltaY
        });
        lastMouseRef.current = coords;
      }
    };
    const handleMouseUp = () => {
      setIsDraggingCenter(false);
      setIsDraggingSun(false);
      lastMouseRef.current = null;
      svgRef.current = null;
    };
    if (isDraggingCenter || isDraggingSun) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingCenter, isDraggingSun, centerX, centerY, onCenterChange, onSunPositionChange]);
  useEffect(() => {
    const findSVG = () => {
      const element = document.querySelector('svg');
      if (element) {
        svgRef.current = element;
      }
    };
    findSVG();
  }, []);
  const handleIncreaseRadius = () => {
    const newRadius = Math.min(radius + 50, 800);
    if (onRadiusChange) onRadiusChange(newRadius);
  };
  const handleDecreaseRadius = () => {
    const newRadius = Math.max(radius - 50, 300);
    if (onRadiusChange) onRadiusChange(newRadius);
  };
  const angleRad = sunPosition * Math.PI / 180;
  const sunX = centerX + radius * Math.cos(angleRad);
  const sunY = centerY + radius * Math.sin(angleRad);
  return <g data-id="element-471">
      <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="#FFA726" strokeWidth="2" strokeDasharray="10,10" opacity={0.3} data-id="element-472" />

      <g onMouseDown={e => {
      e.stopPropagation();
      const gElement = e.currentTarget as SVGGElement;
      const svg = gElement.ownerSVGElement;
      if (svg) svgRef.current = svg;
      setIsDraggingSun(true);
    }} style={{
      cursor: isDraggingSun ? 'grabbing' : 'grab'
    }} data-id="element-473">
        <motion.circle cx={sunX} cy={sunY} r={28} fill="#FFA726" stroke="#FF9800" strokeWidth={isDraggingSun ? 4 : 3} filter="drop-shadow(0 0 12px rgba(255, 167, 38, 0.8))" whileHover={{
        scale: 1.1
      }} animate={{
        scale: isDraggingSun ? 1.15 : 1
      }} data-id="element-474" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
        const rayAngle = angle * Math.PI / 180;
        const rayLength = 15;
        const startX = sunX + 28 * Math.cos(rayAngle);
        const startY = sunY + 28 * Math.sin(rayAngle);
        const endX = sunX + (28 + rayLength) * Math.cos(rayAngle);
        const endY = sunY + (28 + rayLength) * Math.sin(rayAngle);
        return <line key={angle} x1={startX} y1={startY} x2={endX} y2={endY} stroke="#FF9800" strokeWidth="3" strokeLinecap="round" data-id="element-475" />;
      })}
      </g>

      <foreignObject x={centerX - 90} y={centerY - radius - 60} width={180} height={40} data-id="element-476">
        <div className="flex gap-2 items-center justify-center" xmlns="http://www.w3.org/1999/xhtml" data-id="element-477">
          <button onMouseDown={handleCenterMouseDown} className="w-8 h-8 bg-[#2D2D2D] border border-[#66BB6A] rounded-lg flex items-center justify-center text-[#66BB6A] hover:bg-[#3A3A3A] transition-colors pointer-events-auto" style={{
          cursor: isDraggingCenter ? 'grabbing' : 'grab'
        }} data-id="element-478">
            <Move size={16} data-id="element-479" />
          </button>
          <button onClick={handleDecreaseRadius} className="w-8 h-8 bg-[#2D2D2D] border border-[#FFA726] rounded-lg flex items-center justify-center text-[#FFA726] hover:bg-[#3A3A3A] transition-colors pointer-events-auto" data-id="element-480">
            <Minus size={16} data-id="element-481" />
          </button>
          <button onClick={handleIncreaseRadius} className="w-8 h-8 bg-[#2D2D2D] border border-[#FFA726] rounded-lg flex items-center justify-center text-[#FFA726] hover:bg-[#3A3A3A] transition-colors pointer-events-auto" data-id="element-482">
            <Plus size={16} data-id="element-483" />
          </button>
        </div>
      </foreignObject>

      <text x={centerX} y={centerY - radius - 80} fill="#FFA726" fontSize={18} fontWeight="700" fontFamily="Inter, sans-serif" textAnchor="middle" data-id="element-484">
        {isDraggingSun ? '☀️ Dragging...' : `Sunrise Location: ${Math.round(sunPosition)}°`}
      </text>
    </g>;
}